import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux';
import { updateClinician } from '../redux/clinic/clinicSlice';
import useFetchDetails from '../hooks/Clinic/useFetchClinicianDetails';
import ClinicianAdminEdit from '../components/clinician/ClinicianAdminEdit';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

// Type for the clinician data expected by ClinicianAdminEdit
interface ClinicianData {
  _id: string;
  clinicianId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  emailSource: string;
  npiNumber: string;
  titles: string[]; // Changed from title to titles array
  role: string;
  status: string;
  phone: string;
  specialties: string[];
  licenses: string[]; // Additional Certifications
  licenseDetails: { number: string; type: string; state: string }[]; // State Licenses
  slug: string;
  isActive: boolean;
  id_practice: string;
  createdAt: string;
  updatedAt: string;
  gender?: string;
  ethnicity?: string;
  experience?: string;
  education?: string[]; // Changed to array
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  profile: {
    detailsScrapedAt: string;
    detailsSource: string;
    editUrl: string;
    bio?: string;
    profileImage?: string;
    vslVideo?: string; // Added vslVideo
    maxSessions?: { perWeek?: number };
    rates?: Array<{ sessionType: string; amount: number; currency: string }>;
    sessionTypes?: Array<{ type: string; duration: number }>;
    insuranceAccepted?: string[];
    languages?: string[];
    modalities?: string[];
    ageRanges?: string[];
    sessionLength?: string;
    sessionPrice?: string;
  };
}

const ClinicianEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedClinician, isLoading, error, fetchClinicianData } = useFetchDetails();
  const [practiceLocations, setPracticeLocations] = useState<any[]>([]);

  // Add loading and feedback states
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (id) {
      fetchClinicianData(id);
    }
  }, [id, fetchClinicianData]);

  // Fetch practice locations
  useEffect(() => {
    const fetchPracticeLocations = async () => {
      if (!selectedClinician?.id_practice) return;
      try {
        const response = await api.get(`/practice/${selectedClinician.id_practice}`);
        const practice = response.data?.body || response.data;
        if (practice?.locations) {
          setPracticeLocations(practice.locations);
        }
      } catch (err) {
        console.error('Failed to fetch practice locations:', err);
      }
    };
    fetchPracticeLocations();
  }, [selectedClinician?.id_practice]);

  // Convert Clinician to ClinicianData format
  const convertToClinicianData = (clinician: any): ClinicianData => {
    return {
      _id: clinician._id || clinician.id || '',
      clinicianId: clinician.clinicianId || clinician._id || clinician.id || '',
      firstName: clinician.firstName || '',
      lastName: clinician.lastName || '',
      fullName: clinician.fullName || `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim(),
      email: clinician.email || '',
      emailSource: clinician.emailSource || '',
      npiNumber: clinician.npiNumber || '',
      titles: Array.isArray(clinician.titles)
        ? clinician.titles
        : clinician.title ? [clinician.title] : [],
      role: clinician.role || '', // This maps to "Professional Title" in UI
      status: clinician.status || 'pending',
      phone: clinician.phone || '',
      specialties: Array.isArray(clinician.specialties)
        ? clinician.specialties.map((s: any) => typeof s === 'string' ? s : s.name || s)
        : [],
      licenses: clinician.licenses || [], // Certifications
      licenseDetails: (() => {
        // Backend schema: licenses: [{ number, type, state }]
        if (Array.isArray(clinician.licenses) && clinician.licenses.length > 0 && typeof clinician.licenses[0] === 'object') {
          return clinician.licenses.map((l: any) => ({
            number: String(l.number || ''),
            type: String(l.type || ''),
            state: String(l.state || ''),
          }));
        }
        // Legacy fallback: state_licenses: [{ license_number, license_type }]
        if (Array.isArray(clinician.state_licenses)) {
          return clinician.state_licenses.map((l: any) => ({
            number: String(l.license_number || ''),
            type: String(l.license_type || ''),
            state: String(l.state || ''),
          }));
        }
        return [{ number: '', type: '', state: '' }];
      })(),
      slug: clinician.slug || '',
      isActive: clinician.isActive || false,
      id_practice: clinician.id_practice || '',
      createdAt: clinician.createdAt || '',
      updatedAt: clinician.updatedAt || '',
      gender: clinician.gender,
      ethnicity: clinician.ethnicity,
      experience: clinician.experience,
      education: Array.isArray(clinician.education) ? clinician.education : [],
      // Map clinician.location[] (backend schema) → address.locations[]
      address: (() => {
        const rawLocs = Array.isArray(clinician.location) ? clinician.location : [];
        const locations = rawLocs.map((loc: any) => ({
          id_location: loc.id_location != null ? String(loc.id_location) : '',
          name: loc.name != null ? String(loc.name) : '',
          postal_code: loc.postal_code != null ? String(loc.postal_code) : '',
        }));
        return { locations: locations.length > 0 ? locations : [{ id_location: '', name: '', postal_code: '' }] };
      })(),
      profile: {
        detailsScrapedAt: clinician.profile?.detailsScrapedAt || '',
        detailsSource: clinician.profile?.detailsSource || '',
        editUrl: clinician.profile?.editUrl || '',
        // Map top-level fields to profile fields as fallback
        bio: clinician.bio || clinician.profile?.bio || '',
        profileImage: clinician.profileImage || clinician.profile?.profileImage || '',
        vslVideo: clinician.vsl_url || clinician.profile?.vsl_url || '',

        // Handle Rates: map {type, amount} to {sessionType, amount, currency}
        rates: (clinician.rates || clinician.profile?.rates || []).map((r: any) => ({
          sessionType: r.sessionType || r.type,
          amount: Number(r.amount),
          currency: r.currency || 'USD'
        })),

        // Handle Session Types: map strings to objects if needed
        sessionTypes: (clinician.sessionTypes || clinician.profile?.sessionTypes || []).map((s: any) =>
          typeof s === 'string' ? { type: s, duration: 50 } : s
        ),

        // Map maxSessions from root level (if available)
        maxSessions: clinician.maxSessions || { perWeek: undefined },

        insuranceAccepted: clinician.insuranceAccepted || clinician.profile?.insuranceAccepted || [],
        languages: clinician.languages || clinician.profile?.languages || ['English'],

        // Map API 'modality' to UI 'modalities'
        modalities: clinician.modality || clinician.profile?.modalities || [],

        // Map API 'ageRange' to UI 'ageRanges'
        ageRanges: clinician.ageRange || clinician.profile?.ageRanges || [],

        sessionLength: clinician.profile?.sessionLength || clinician.sessionLength,
        sessionPrice: clinician.profile?.sessionPrice || clinician.sessionPrice
      }
    };
  };

  const handleSave = async (updatedClinician: ClinicianData) => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Send location back as the backend schema array: [{ id_location, name, postal_code }]
      const locationPayload = (updatedClinician.address?.locations || [])
        .filter(loc => loc.name || loc.postal_code)
        .map(loc => ({
          id_location: loc.id_location || '',
          name: loc.name || '',
          postal_code: loc.postal_code || '',
        }));

      // Transform to update payload matching VerificationPage structure
      const updateData: any = {
        first_name: updatedClinician.firstName,
        last_name: updatedClinician.lastName,
        name: `${updatedClinician.firstName} ${updatedClinician.lastName}`.trim(),
        email: updatedClinician.email,
        phone_number: updatedClinician.phone,
        titles: updatedClinician.titles,
        location: locationPayload,
        zip_code: updatedClinician.address?.locations?.[0]?.postal_code || undefined,

        npiNumber: updatedClinician.npiNumber,
        role: updatedClinician.role,

        practice: {
          name: updatedClinician.fullName,
        },

        bio: updatedClinician.profile.bio,
        profileImage: updatedClinician.profile.profileImage,
        vsl_url: updatedClinician.profile.vslVideo,
        languages: updatedClinician.profile.languages,
        insuranceAccepted: updatedClinician.profile.insuranceAccepted,
        sessionTypes: updatedClinician.profile.sessionTypes,
        modality: updatedClinician.profile.modalities,
        ageRange: updatedClinician.profile.ageRanges,
        rates: updatedClinician.profile.rates,
        education: updatedClinician.education,

        // licenses: [{ number, type, state }] — matches backend schema
        licenses: updatedClinician.licenseDetails
          .filter(l => l.number.trim() || l.type.trim() || l.state.trim())
          .map(l => ({ number: l.number, type: l.type, state: l.state })),

        specialties: updatedClinician.specialties,
        gender: updatedClinician.gender,

        status: updatedClinician.status,
        isActive: updatedClinician.isActive,

        licenses: updatedClinician.licenses,
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });


      if (!id) {
        throw new Error('Clinician ID is missing');
      }
      await dispatch(updateClinician({ clinicianId: id, updateData })).unwrap();

      // Show success message without redirect
      setSaveMessage({ type: 'success', message: 'Clinician updated successfully!' });

      // Optionally refresh data to reflect saved changes
      if (id) {
        await fetchClinicianData(id);
      }

    } catch (error) {
      console.error('Failed to update clinician:', error);
      setSaveMessage({ type: 'error', message: 'Failed to update clinician. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/practice/clinician/details/${id}`);
  };

  // Memoize so the object reference only changes when selectedClinician actually changes.
  // Without this, convertToClinicianData creates a new object on every parent render,
  // causing the useEffect([clinician]) in ClinicianAdminEdit to fire and reset the form
  // mid-edit (e.g. when isSaving flips to true).
  const clinicianData = useMemo(
    () => (selectedClinician ? convertToClinicianData(selectedClinician) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedClinician]
  );

  return <>
    <DashboardLayout>
      {/* Loading/Success/Error Messages */}
      {isSaving && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" />
            <div>
              <p className="font-medium">Updating Clinician...</p>
              <p className="text-sm opacity-90">Please wait while we save your changes</p>
            </div>
          </div>
        </div>
      )}

      {saveMessage && (
        <div className={`fixed top-4 right-4 px-6 py-4 rounded-lg shadow-lg z-50 max-w-md ${saveMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
          <div className="flex items-center gap-3">
            {saveMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <div>
              <p className="font-medium">
                {saveMessage.type === 'success' ? 'Success!' : 'Error!'}
              </p>
              <p className="text-sm opacity-90">{saveMessage.message}</p>
            </div>
          </div>
        </div>
      )}

      {clinicianData && (
        <ClinicianAdminEdit
          key={clinicianData._id || 'clinician-edit'}
          clinician={clinicianData}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
          practiceLocations={practiceLocations}
        />
      )}
    </DashboardLayout>
  </>
}

export default ClinicianEditPage;
